/**
 * Contact form progressive enhancement. The form submits natively without
 * JavaScript (method="POST" to the form endpoint). Here we layer inline
 * validation feedback and an async submit with a friendly status message,
 * never removing the native HTML5 validation baseline.
 */
// Accepts any reasonable US phone number: optional leading +1/1, then a
// 10-digit number written with spaces, dots, dashes, or parentheses.
const PHONE_PATTERN = /^(\+?1[\s.\-]?)?\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4}$/;

declare global {
  interface Window {
    turnstile?: {
      reset: (widgetIdOrContainer?: string | HTMLElement) => void;
    };
  }
}

export function initContactForm(): void {
  const form = document.getElementById('contact-form') as HTMLFormElement | null;
  const status = document.getElementById('form-status');
  if (!form || !status) return;

  const phone = form.querySelector<HTMLInputElement>('#phone');

  const setStatus = (message: string, tone: 'info' | 'error' | 'success') => {
    status.textContent = message;
    status.classList.remove('text-pine', 'text-red-700', 'text-green-700');
    status.classList.add(
      tone === 'error' ? 'text-red-700' : tone === 'success' ? 'text-green-700' : 'text-pine'
    );
  };

  // Phone is optional, so only flag it when something is entered but it
  // doesn't look like a US phone number.
  const validatePhone = () => {
    if (!phone) return;
    const value = phone.value.trim();
    const isValid = value === '' || PHONE_PATTERN.test(value);
    phone.setCustomValidity(isValid ? '' : 'Enter a valid phone number, e.g. (123) 456-7890.');
  };

  phone?.addEventListener('input', validatePhone);

  form.addEventListener('submit', async (event) => {
    // Native validation first.
    validatePhone();
    if (!form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
      setStatus('Please complete the required fields.', 'error');
      return;
    }

    // Honeypot: silently drop bot submissions.
    const honeypot = form.querySelector<HTMLInputElement>('input[name="_gotcha"]');
    if (honeypot && honeypot.value.trim() !== '') {
      event.preventDefault();
      return;
    }

    const action = form.getAttribute('action') ?? '';

       // Enhanced async submission.
    event.preventDefault();
    const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submitButton) submitButton.disabled = true;
    setStatus('Sending your message...', 'info');

    try {
      const payload = Object.fromEntries(new FormData(form).entries());
      if (payload.dueDate === '') delete payload.dueDate;
      const response = await fetch(action, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        form.reset();
        setStatus('Thank you. We received your message and will be in touch shortly.', 'success');
      } else {
        setStatus(
          'Something went wrong. Please email luxedoulaservices@gmail.com or call (310) 779-5991.',
          'error'
        );
      }
    } catch {
      setStatus(
        'We could not send your message. Please email luxedoulaservices@gmail.com or call (310) 779-5991.',
        'error'
      );
    } finally {
      if (submitButton) submitButton.disabled = false;
      window.turnstile?.reset();
    }
  });
}
